/**
 * Mixin for Action handling used in Form and ItemTree component
 */
import {v4 as uuidV4} from 'uuid';
import parse from 'html-react-parser';
import {keys} from 'ts-transformer-keys';
import axios, {AxiosResponse} from 'axios';

import {confirmPopup} from 'primereact/confirmpopup';

import MetaModelResolver from '~/shared/src/utilities/MetaModelResolver';
import UserTemplateStringResolver from '~/shared/src/utilities/UserTemplateStringResolver';
import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import Item, {
    ItemAttributesForCreation,
    ItemAttributesSettable
} from '~/shared/src/ci/Item';
import Reference, {
    ReferenceAttributesForCreation,
    ReferenceAttributesSettable
} from '~/shared/src/ci/Reference';
import PropertyResolver from '~/shared/src/utilities/PropertyResolver';
import ReferencePropertyResolver from '~/shared/src/utilities/ReferencePropertyResolver';
import PropertySet from '~/shared/src/mm/PropertySet';
import ReferencePropertySet from '~/shared/src/mm/ReferencePropertySet';
import {ReferenceValue} from '~/shared/src/utilities/dataUtils';
import objectUtils from '~/shared/src/utilities/objectUtils';
import {
    ContainerProperties,
    FormFieldProperties,
    FormProperties,
    TabContainerProperties
} from '~/shared/src/utilities/modules/form';
import {
    LoadItemParameters,
    LoadItemResult,
    LoadQueryResultParameters,
    LoadQueryResultResult
} from '~/shared/src/utilities/modules/data';

import xhr from '~/frontend/src/utilities/xhr';
import {AllProps as ItemTreeProps} from '~/frontend/src/components/base/shared/tree/ItemTree';
import {AllProps as OverviewProps} from '~/frontend/src/components/base/Overview';
import {
    AllProps,
    AllProps as FormProps
} from '~/frontend/src/components/base/Form';
import {getPanelProps} from '~/frontend/src/components/base/page/PageTabPanel/utilities';
import {PanelPropsWithPanelType} from '~/frontend/src/actions/panelManager/openPanel';
import {Parameters} from '~/backend/src/utilities/queryLoader';


/**
 * props used by ActionMixin
 * these must be provided by the extending component
 *
 import {OpenPanel} from '~/frontend/src/actions/panelManager/openPanel';
 import {SetMessage} from '~/frontend/src/actions/intercom';
 import {OpenPanelForItemType} from '~/frontend/src/actions/panelManager/openPanelForItemType';
 import {StartRequest} from '~/frontend/src/actions/ajax/startRequest';
 import {EndRequest} from '~/frontend/src/actions/ajax/endRequest';
 import {UpdateReference} from '~/frontend/src/actions/data/updateReference';
 import {UpdateItem} from '~/frontend/src/actions/data/updateItem';
 import {AddReference} from '~/frontend/src/actions/data/addReference';
 import {AddItem} from '~/frontend/src/actions/data/addItem';
 import {DeleteItem} from '~/frontend/src/actions/data/deleteItem';
 import {DeleteReference} from '~/frontend/src/actions/data/deleteReference';

 props : {
 openPanel : OpenPanel;
 openPanelForItemType : OpenPanelForItemType;

 startRequest : StartRequest;
 endRequest : EndRequest;

 setMessage : SetMessage;

 addItem : AddItem;
 deleteItem : DeleteItem;
 updateItem : UpdateItem;
 addReference : AddReference;
 deleteReference : DeleteReference;
 updateReference : UpdateReference;
 }
 **/

const debugColorScheme = {
    buildDynamicFunction               : '3F51B5',
    applyDynamicFunctionDataChanges    : '3F51B5',
    applyAdditionalReferenceDataChanges: '3F51B5'
};


export type FunctionParameters = {
    [ name : string ] : any;
};

export type ResolvedDataObject = {
    item : Item;
    property : ProxyHandler<PropertyResolver>;
    reference? : Reference;
    referenceProperty? : ProxyHandler<ReferencePropertyResolver>;
};

export type ResolvedDataObjectState = {
    itemId : number;
    item : string;
    referenceId : number;
    reference : string;
};

export type ResolvedDataObjectInfo = {
    dataContext : string;
    itemType : string;
    referenceSetId : number;
    referenceDirection : 'SrcToDest' | 'DestToSrc';
    otherItem? : Item;
    otherItemType : string;
    state : ResolvedDataObjectState | ResolvedDataObjectState[]
};

export type MappedResolvedDataObjects = {
    [ label : string ] : ResolvedDataObject | ResolvedDataObject[];
};

export type ResolvedDataObjects = MappedResolvedDataObjects & {
    [ infoLabel : `${keyof MappedResolvedDataObjects}Info` ] : ResolvedDataObjectInfo;
};
export type ResolvedLayoutElements = {
    [ label : string ] : FormProperties | TabContainerProperties | ContainerProperties | FormFieldProperties;
};

export type ClonedObjectCache = {
    items : {
        [ id : number ] : Item;
    };
    references : {
        [ id : number ] : Reference;
    };
    originalStates : {
        items : {
            [ id : number ] : string;
        };
        references : {
            [ id : number ] : string;
        };
    };
};

export type ClonedObjectsIdMappings = {
    items : {
        [ id : number ] : number;
    };
    references : {
        [ id : number ] : number;
    };
};

type ComponentProps = FormProps | ( ItemTreeProps & { viewKey? : never } ) | OverviewProps & { additionalElusiveDataContexts? : never };

const nodeJsGlobalKeys   = keys<NodeJS.Global>() as string[],
      unsetGlobals       = ( ( [] as string[] )
          .concat( nodeJsGlobalKeys )
          .concat( Object.keys( global ) )
          .reduce(
              ( reducedArray, gP ) => {
                  if ( gP !== 'global' && String( Number( gP ) ) !== gP && !Object.keys( reducedArray ).includes( gP ) ) {
                      reducedArray[ gP ] = undefined;
                  }

                  return reducedArray;
              },
              {} as { [ parameter : string ] : any }
          ) ),
      unsetGlobalsKeys   = Object.keys( unsetGlobals ),
      unsetGlobalsValues = Object.values( unsetGlobals );

export const actionMixin = {

    /**
     * Builds a dynamic function.
     *
     * @param functionParameters
     * @param functionBody
     * @param resolvedDataObjects
     * @param props
     * @param event
     *
     * @private
     */
    buildDynamicFunction( functionParameters : FunctionParameters, functionBody : string, resolvedDataObjects : ResolvedDataObjects, props : ComponentProps = this.props, event? : any ) : () => Promise<any> {
        const startTime                           = this.debug ? performance.now() : 0,
              awaitableDynamicHelperFunctionNames = [ 'addExistingItem', 'loadQuery', 'showConfirm' ],
              awaitInFrontRegExp                  = new RegExp( `\\bawait\\s*$` ),
              awaitInsert                         = 'await ';

        // cache global parameters
        if ( !this.dynamicFunctionGlobalParameters ) {
            const viewKey          = props.viewKey,
                  uidParts         = props.uid.split( '_' ),
                  parentPanelProps = getPanelProps( uidParts[ 0 ] )?.panelProps,
                  parentViewKey    = parentPanelProps?.viewKey ?? parentPanelProps?.treeViewKey,
                  directLinkUrl    = global.directLinkUrl ? new URL( global.directLinkUrl ) : undefined;

            this.variables = { ...props.elusiveData?.variables };

            //delete axios.defaults.headers.common[ 'X-Requested-With' ];

            this.dynamicFunctionGlobalParameters = {
                document: {},
                global  : {},
                window  : {},
                // list allowed things here,
                // be carefully with objects mounted into the dynamic function
                // if you not want the dynamic function to change them, clone or proxify them
                axios,
                baseUrl  : BASE_URL,
                metaModel: new MetaModelResolver(),
                user     : new UserTemplateStringResolver( props.user ),
                config   : JSON.parse( JSON.stringify( config ) ),
                variables: this.variables,
                viewKey,
                parentViewKey,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                __,
                url: {
                    href        : directLinkUrl?.href,
                    host        : directLinkUrl?.host,
                    hostname    : directLinkUrl?.hostname,
                    protocol    : directLinkUrl?.protocol,
                    port        : directLinkUrl?.port,
                    origin      : directLinkUrl?.origin,
                    pathname    : directLinkUrl?.pathname,
                    hash        : directLinkUrl?.hash,
                    searchParams: directLinkUrl?.searchParams ? Array.from( directLinkUrl?.searchParams?.keys() ).reduce(
                        ( innerRetVal, parameter ) => {
                            innerRetVal[ parameter ] = directLinkUrl.searchParams.get( parameter );

                            return innerRetVal;
                        },
                        {} as { [ parameter : string ] : any }
                    ) : {}
                }
            };

            delete this.dynamicFunctionGlobalParameterKeys;
            delete this.dynamicFunctionGlobalParameterValues;
        }
        if ( !this.dynamicFunctionGlobalParameterKeys ) {
            this.dynamicFunctionGlobalParameterKeys = ( unsetGlobalsKeys as string[] ).concat( Object.keys( this.dynamicFunctionGlobalParameters ) as string[] );
        }
        if ( !this.dynamicFunctionGlobalParameterValues ) {
            this.dynamicFunctionGlobalParameterValues = unsetGlobalsValues.concat( Object.values( this.dynamicFunctionGlobalParameters ) );
        }

        const dynamicHelperFunctions = this.getDynamicHelperFunctions( resolvedDataObjects, event ),
              parameterKeys          = ( Object.keys( functionParameters ) as string[] ).concat( this.dynamicFunctionGlobalParameterKeys ).concat( Object.keys( dynamicHelperFunctions ) as string [] ),
              parameterValues        = Object.values( functionParameters ).concat( this.dynamicFunctionGlobalParameterValues ).concat( Object.values( dynamicHelperFunctions ) );

        // add await for dynamic helper functions
        for ( const awaitableDynamicHelperFunctionName of awaitableDynamicHelperFunctionNames ) {
            const dHFRegExp = new RegExp( `\\b${awaitableDynamicHelperFunctionName}\\b`, 'g' );

            let match : RegExpMatchArray | null = dHFRegExp.exec( functionBody ),
                count                           = 0;

            while ( match?.index !== undefined && count++ < 100 ) {
                if ( !awaitInFrontRegExp.test( functionBody.substring( 0, match.index ) ) ) {
                    functionBody = `${functionBody.substring( 0, match.index )}${awaitInsert}${functionBody.substring( match.index )}`;
                }

                match = dHFRegExp.exec( functionBody );
            }
        }

        const retVal = () => {
            return new Promise( ( resolve, reject ) => {
                new Function(
                    'wrapperResolve',
                    'wrapperReject',
                    ...parameterKeys,
                    //
                    `with ( wrapperResolve, wrapperReject, ${parameterKeys.join( ', ' )} ) {\n` +
                    '    ( async() => {\n' +
                    '        try {\n' +
                    `            const retVal = await ( async () => {\n` +
                    `                     ${functionBody}\n` +
                    `                  } )();\n` +
                    '\n' +
                    '            wrapperResolve( retVal ?? true );\n' +
                    '        }\n' +
                    '        catch ( e ) {\n' +
                    '            wrapperReject( e );\n' +
                    '        }\n' +
                    '    } )();\n' +
                    '}'
                ).bind(
                    null,
                    resolve,
                    reject,
                    ...parameterValues
                )();
            } );
        };

        if ( this.debug ) {
            console.log( `%cActionMixin - %cbuildDynamicFunction%c - ${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 98 ), `font-weight: bold;`, `color: #${debugColorScheme.buildDynamicFunction}; font-weight: bold;`, 'color: unset; font-weight: bold;', 'font-weight: unset;' );
        }

        return retVal;
    },

    /**
     * Builds helper functions for use in dynamic function.
     *
     * @param resolvedDataObjects
     *
     * @private
     */
    getDynamicHelperFunctions( resolvedDataObjects : ResolvedDataObjects, event? : any ) : { [ helperFunction : string ] : Function } {
        const commonErrorPrefix = 'error calling helper function ',
              props             = this.props as FormProps & ItemTreeProps,
              {
                  uid,
                  dataContext,
                  schema,
                  mode
              }                 = props,
              deletedStatusId   = metaModel[ schema ].getStatus( 'deleted' )!.id;

        return {

            // TODO: implement confirm - async ! see this.setState( { dynamicFunctionErrors: errorMessages } );
            // TODO: implement addReference ( ??? )

            // frontend specific
            // TODO: discuss signature - otherItem? : Item -> otherObject : ResolvedDataObject | ResolvedDataObject[], index? : number
            addExistingItem: async ( object : ResolvedDataObject | ResolvedDataObject[], arg2 : Item | string, otherItem? : Item ) : Promise<void> => {
                const startTime   = this.debug ? performance.now() : 0,
                      errorPrefix = `${commonErrorPrefix}addExistingItem: `;

                if ( this.debug ) {
                    console.groupCollapsed( `%c${'helperFunction - %caddExistingItem%c'.asLogLine( 94 )}`, 'font-weight: normal;', `font-weight: bold;`, 'font-weight: normal;' );
                    console.log( '    object', Array.isArray( object ) ? ( [] as ResolvedDataObject[] ).concat( object ) : { ...object } );
                    console.log( '    key | item', arg2 );
                    console.log( '    otherItem', otherItem );
                }

                // parameter validation
                if ( !object ) {
                    console.groupEnd();
                    throw ( new Error( `${errorPrefix}first parameter (object) must contain a ResolvedDataObject` ) );
                }
                if ( !arg2 ) {
                    console.groupEnd();
                    throw ( new Error( `${errorPrefix}first parameter (item|key) must contain an Item or a string` ) );
                }

                const dataObjectLabel = Object.keys( resolvedDataObjects ).find(
                          ( dOL ) => {
                              if ( resolvedDataObjects[ dOL ] === object ) {
                                  return true;
                              }
                              else {
                                  return false;
                              }
                          }
                      ),
                      dataObjectInfo  = resolvedDataObjects[ `${dataObjectLabel}Info` ] as ResolvedDataObjectInfo;

                // logical validation
                if ( !dataObjectLabel || !dataObjectInfo ) {
                    console.groupEnd();
                    throw ( new Error( `${errorPrefix}first parameter (object) must contain a ResolvedDataObject` ) );
                }

                const itemType     = metaModel[ schema ].getItemType( dataObjectInfo.itemType ),
                      referenceSet = metaModel[ schema ].getReferenceSet( dataObjectInfo.referenceSetId );

                // logical validation
                if ( !itemType ) {
                    console.groupEnd();
                    throw ( new Error( `${errorPrefix}internal error: can't resolve ItemType` ) );
                }
                if ( !referenceSet ) {
                    console.groupEnd();
                    throw ( new Error( `${errorPrefix}internal error: can't resolve ReferenceSet` ) );
                }
                if ( !dataObjectInfo.otherItem && !otherItem ) {
                    console.groupEnd();
                    throw ( new Error( `${errorPrefix}internal error: can't resolve reference target` ) );
                }
                if ( !dataObjectInfo.otherItem && otherItem && ( !( otherItem instanceof Item ) || dataObjectInfo.otherItemType !== otherItem.itemType.name ) ) {
                    console.groupEnd();
                    throw ( new Error( `${errorPrefix}third parameter (item) must contain an item of the same ItemType as the ResolvedDataObject is referenced to` ) );
                }

                if ( dataObjectInfo.otherItem ) {
                    otherItem = dataObjectInfo.otherItem;
                }

                if ( otherItem ) {
                    let newItem : Item | undefined;

                    // load item from backend
                    if ( typeof arg2 === 'string' ) {
                        const key                         = arg2,
                              itemTypeName                = dataObjectInfo.itemType,
                              params : LoadItemParameters = {
                                  schema,
                                  status: 'all-non-history',

                                  itemTypeName,
                                  key
                              };

                        let response : AxiosResponse<any, any> | null = null;

                        try {
                            response = await xhr.post(
                                `${BASE_URL}/api/data/loadItem`,
                                params,
                                undefined,
                                uid + '_action',
                                'addExistingItem',
                                undefined,
                                'throw'
                            );
                        }
                        catch ( e ) {
                            // do nothing
                        }

                        if ( response?.data ) {
                            const item = ( response.data as LoadItemResult ).item;

                            // logical validation
                            if ( !item ) {
                                console.groupEnd();
                                throw ( new Error( `${errorPrefix}loading existing item from backend returns no item` ) );
                            }

                            newItem = new Item( item, dataObjectInfo.dataContext, schema );
                        }
                        else {
                            console.groupEnd();
                            throw ( new Error( `${errorPrefix}loading existing item from backend returns no result` ) );
                        }
                    }
                    else {
                        newItem = arg2;
                    }

                    if ( newItem ) {

                        // TODO: add cardinality check (backend + frontend) -> see form/index.tsx 5257ff

                        const newReference = new Reference(
                            {
                                referenceSetId: referenceSet.id,
                                srcItemId     : dataObjectInfo.referenceDirection === 'SrcToDest'
                                    ? otherItem.id
                                    : newItem.id,
                                destItemId    : dataObjectInfo.referenceDirection === 'SrcToDest'
                                    ? newItem.id
                                    : otherItem.id
                            },
                            dataObjectInfo.dataContext,
                            schema
                        );

                        if ( !Array.isArray( object ) ) {
                            object.item = newItem;
                            object.property = new PropertyResolver( newItem );
                            object.reference = newReference;
                            object.referenceProperty = new ReferencePropertyResolver( newReference );

                            ( dataObjectInfo.state as ResolvedDataObjectState ) = {
                                itemId     : 0,
                                item       : '',
                                referenceId: 0,
                                reference  : ''
                            };
                        }
                        else {
                            object.push( {
                                item             : newItem,
                                property         : new PropertyResolver( newItem ),
                                reference        : newReference,
                                referenceProperty: new ReferencePropertyResolver( newReference )
                            } );

                            ( dataObjectInfo.state as ResolvedDataObjectState[] ).push( {
                                itemId     : 0,
                                item       : '',
                                referenceId: 0,
                                reference  : ''
                            } );
                        }
                    }

                    if ( this.debug ) {
                        console.log( '    changed object', Array.isArray( object ) ? ( [] as ResolvedDataObject[] ).concat( object ) : { ...object } );
                        console.log( `%c${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 93 ), `font-weight: bold;`, 'font-weight: unset;' );
                        console.groupEnd();
                    }
                }
            },

            // frontend specific
            // TODO: discuss signature - otherItem : Item -> otherObject : ResolvedDataObject | ResolvedDataObject[], index? : number
            // TODO: ENHANCEMENT -> update dataObjectInfo for all subsequently referenced items to be able to create chained items easier ( A -> B -> C: addItem( B ); addItem( C ) instead of addItem( B ); addItem( C, undefined, B ) )
            addItem: ( object : ResolvedDataObject | ResolvedDataObject[], key? : string, otherItem? : Item ) : void => {
                const startTime   = this.debug ? performance.now() : 0,
                      errorPrefix = `${commonErrorPrefix}addItem: `;

                if ( this.debug ) {
                    console.groupCollapsed( `%c${'helperFunction - %caddItem%c'.asLogLine( 94 )}`, 'font-weight: normal;', `font-weight: bold;`, 'font-weight: normal;' );
                    console.log( '    object', JSON.parse( JSON.stringify( object ) ) );
                    console.log( '    key', key );
                    console.log( '    otherItem', JSON.parse( JSON.stringify( otherItem ?? {} ) ) );
                }

                // parameter validation
                if ( !object ) {
                    console.groupEnd();
                    throw ( new Error( `${errorPrefix}first parameter must contain a ResolvedDataObject` ) );
                }

                const dataObjectLabel = Object.keys( resolvedDataObjects ).find(
                          ( dOL ) => {
                              if ( resolvedDataObjects[ dOL ] === object ) {
                                  return true;
                              }
                              else {
                                  return false;
                              }
                          }
                      ),
                      dataObjectInfo  = resolvedDataObjects[ `${dataObjectLabel}Info` ] as ResolvedDataObjectInfo;

                // logical validation
                if ( !dataObjectLabel || !dataObjectInfo ) {
                    console.groupEnd();
                    throw ( new Error( `${errorPrefix}first parameter (object) must contain a ResolvedDataObject` ) );
                }

                const itemType     = metaModel[ schema ].getItemType( dataObjectInfo.itemType ),
                      referenceSet = metaModel[ schema ].getReferenceSet( dataObjectInfo.referenceSetId );

                // logical validation
                if ( !itemType ) {
                    console.groupEnd();
                    throw ( new Error( `${errorPrefix}internal error: can't resolve ItemType` ) );
                }
                if ( !referenceSet ) {
                    console.groupEnd();
                    throw ( new Error( `${errorPrefix}internal error: can't resolve ReferenceSet` ) );
                }
                if ( !itemType?.isKeyAutogenerated && !key ) {
                    console.groupEnd();
                    throw ( new Error( `${errorPrefix}second parameter (key) must contain a string as the key of the item to add` ) );
                }
                if ( !dataObjectInfo.otherItem && !otherItem ) {
                    console.groupEnd();
                    throw ( new Error( `${errorPrefix}internal error: can't resolve reference target` ) );
                }
                if ( !dataObjectInfo.otherItem && otherItem && ( !( otherItem instanceof Item ) || dataObjectInfo.otherItemType !== otherItem.itemType.name ) ) {
                    console.groupEnd();
                    throw ( new Error( `${errorPrefix}third parameter (item) must contain an item of the same ItemType as the ResolvedDataObject is referenced to` ) );
                }

                if ( dataObjectInfo.otherItem ) {
                    otherItem = dataObjectInfo.otherItem;
                }

                if ( otherItem ) {

                    // TODO: add cardinality check (backend + frontend) -> see form/index.tsx 5257ff

                    const newItem      = new Item(
                              {
                                  itemTypeId: itemType.id,
                                  key
                              },
                              dataObjectInfo.dataContext,
                              schema
                          ),
                          newReference = new Reference(
                              {
                                  referenceSetId: referenceSet.id,
                                  srcItemId     : dataObjectInfo.referenceDirection === 'SrcToDest'
                                      ? otherItem.id
                                      : newItem.id,
                                  destItemId    : dataObjectInfo.referenceDirection === 'SrcToDest'
                                      ? newItem.id
                                      : otherItem.id
                              },
                              dataObjectInfo.dataContext,
                              schema
                          );

                    if ( !Array.isArray( object ) ) {
                        object.item = newItem;
                        object.property = new PropertyResolver( newItem );
                        object.reference = newReference;
                        object.referenceProperty = new ReferencePropertyResolver( newReference );

                        ( dataObjectInfo.state as ResolvedDataObjectState ) = {
                            itemId     : 0,
                            item       : '',
                            referenceId: 0,
                            reference  : ''
                        };
                    }
                    else {
                        object.push( {
                            item             : newItem,
                            property         : new PropertyResolver( newItem ),
                            reference        : newReference,
                            referenceProperty: new ReferencePropertyResolver( newReference )
                        } );

                        ( dataObjectInfo.state as ResolvedDataObjectState[] ).push( {
                            itemId     : 0,
                            item       : '',
                            referenceId: 0,
                            reference  : ''
                        } );
                    }

                    if ( !dataObjectInfo.otherItem ) {
                        dataObjectInfo.otherItem = otherItem;
                    }
                }

                if ( this.debug ) {
                    console.log( `%c${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 93 ), `font-weight: bold;`, 'font-weight: unset;' );
                    console.groupEnd();
                }
            },

            // frontend specific - add backend version
            // TODO: discuss signature - whichObject? : ResolvedDataObject -> index? : number
            deleteItem: ( object : ResolvedDataObject | ResolvedDataObject[], whichObject? : ResolvedDataObject ) : void => {
                const startTime   = this.debug ? performance.now() : 0,
                      errorPrefix = `${commonErrorPrefix}deleteItem: `;

                if ( this.debug ) {
                    console.groupCollapsed( `%c${'helperFunction - %cdeleteItem%c'.asLogLine( 94 )}`, 'font-weight: normal;', `font-weight: bold;`, 'font-weight: normal;' );
                    console.log( '    object', JSON.parse( JSON.stringify( object ) ) );
                    console.log( '    whichObject', JSON.parse( JSON.stringify( whichObject ) ) );
                }

                // parameter validation
                if ( !object ) {
                    console.groupEnd();
                    throw ( new Error( `${errorPrefix}first parameter must contain a ResolvedDataObject` ) );
                }

                const dataObjectLabel = Object.keys( resolvedDataObjects ).find(
                          ( dOL ) => {
                              if ( resolvedDataObjects[ dOL ] === object ) {
                                  return true;
                              }
                              else {
                                  return false;
                              }
                          }
                      ),
                      dataObjectInfo  = resolvedDataObjects[ `${dataObjectLabel}Info` ] as ResolvedDataObjectInfo;

                // logical validation
                if ( !dataObjectLabel || !dataObjectInfo ) {
                    console.groupEnd();
                    throw ( new Error( `${errorPrefix}first parameter (object) must contain a ResolvedDataObject` ) );
                }
                if ( Array.isArray( object ) && ( !whichObject || !object.includes( whichObject ) ) ) {
                    console.groupEnd();
                    throw ( new Error( `${errorPrefix}second parameter (whichObject) must contain the specific ResolvedDataObject contained in object` ) );
                }

                if ( Array.isArray( object ) ) {
                    whichObject!.item.reset();
                    whichObject!.item.statusId = deletedStatusId;

                    if ( whichObject!.reference ) {
                        whichObject!.reference.reset();
                        whichObject!.reference.statusId = deletedStatusId;
                    }
                }
                else {
                    object.item.reset();
                    object.item.statusId = deletedStatusId;

                    if ( object.reference ) {
                        object.reference.reset();
                        object.reference.statusId = deletedStatusId;
                    }
                }

                if ( this.debug ) {
                    console.log( `%c${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 93 ), `font-weight: bold;`, 'font-weight: unset;' );
                    console.groupEnd();
                }
            },

            // frontend and backend common
            deleteProperty: ( object : Item | Reference, propertyTypeName : string ) : void => {
                const startTime   = this.debug ? performance.now() : 0,
                      errorPrefix = `${commonErrorPrefix}deleteProperty: `;

                if ( this.debug ) {
                    console.groupCollapsed( `%c${'helperFunction - %cdeleteProperty%c'.asLogLine( 94 )}`, 'font-weight: normal;', `font-weight: bold;`, 'font-weight: normal;' );
                    console.log( '    object', JSON.parse( JSON.stringify( object ) ) );
                    console.log( '    propertyTypeName', propertyTypeName );
                }

                // parameter validation
                if ( !object || !( object instanceof Item || object instanceof Reference ) ) {
                    console.groupEnd();
                    throw ( new Error( `${errorPrefix}first parameter (item|reference) must be an Item or a Reference` ) );
                }
                if ( typeof propertyTypeName !== 'string' ) {
                    console.groupEnd();
                    throw ( new Error( `${errorPrefix}second parameter (propertyTypeName) must be a string` ) );
                }

                let objectPropertySet : PropertySet | ReferencePropertySet | undefined;

                if ( object instanceof Item ) {
                    objectPropertySet = object.itemType.propertySets.find( pS => pS.name === propertyTypeName );
                }
                else {
                    objectPropertySet = object.referenceSet.propertySets.find( pS => pS.name === propertyTypeName );
                }

                if ( objectPropertySet && object.properties.has( objectPropertySet.propertyTypeId ) ) {
                    const property = object.properties.get( objectPropertySet.propertyTypeId )!;

                    if ( property.isNew() ) {
                        object.properties.delete( objectPropertySet.propertyTypeId );
                    }
                    else {
                        property.reset();
                        property.statusId = deletedStatusId;
                    }
                }

                if ( this.debug ) {
                    console.log( `%c${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 93 ), `font-weight: bold;`, 'font-weight: unset;' );
                    console.groupEnd();
                }
            },

            // frontend specific
            // TODO: discuss signature - whichObject? : ResolvedDataObject -> index? : number
            deleteReference: ( object : ResolvedDataObject | ResolvedDataObject[], whichObject? : ResolvedDataObject ) : void => {
                const startTime   = this.debug ? performance.now() : 0,
                      errorPrefix = `${commonErrorPrefix}deleteReference: `;

                if ( this.debug ) {
                    console.groupCollapsed( `%c${'helperFunction - %cdeleteReference%c'.asLogLine( 94 )}`, 'font-weight: normal;', `font-weight: bold;`, 'font-weight: normal;' );
                    console.log( '    object', JSON.parse( JSON.stringify( object ) ) );
                    console.log( '    whichObject', JSON.parse( JSON.stringify( whichObject ) ) );
                }

                // parameter validation
                if ( !object ) {
                    console.groupEnd();
                    throw ( new Error( `${errorPrefix}first parameter must contain a ResolvedDataObject` ) );
                }

                const dataObjectLabel = Object.keys( resolvedDataObjects ).find(
                          ( dOL ) => {
                              if ( resolvedDataObjects[ dOL ] === object ) {
                                  return true;
                              }
                              else {
                                  return false;
                              }
                          }
                      ),
                      dataObjectInfo  = resolvedDataObjects[ `${dataObjectLabel}Info` ] as ResolvedDataObjectInfo;

                // logical validation
                if ( !dataObjectLabel || !dataObjectInfo ) {
                    console.groupEnd();
                    throw ( new Error( `${errorPrefix}first parameter (object) must contain a ResolvedDataObject` ) );
                }
                if ( !Array.isArray( object ) && !object.reference ) {
                    console.groupEnd();
                    throw ( new Error( `${errorPrefix}first parameter (object) must contain a ResolvedDataObject` ) );
                }
                if ( Array.isArray( object ) && ( !whichObject || !object.includes( whichObject ) || !whichObject.reference ) ) {
                    console.groupEnd();
                    throw ( new Error( `${errorPrefix}second parameter (whichObject) must contain the specific ResolvedDataObject contained in object` ) );
                }

                if ( Array.isArray( object ) ) {
                    whichObject!.reference!.reset();
                    whichObject!.reference!.statusId = deletedStatusId;
                }
                else {
                    object.reference!.reset();
                    object.reference!.statusId = deletedStatusId;
                }

                if ( this.debug ) {
                    console.log( `%c${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 93 ), `font-weight: bold;`, 'font-weight: unset;' );
                    console.groupEnd();
                }
            },

            // frontend specific
            openLink: ( url : string, target : '_self' | '_blank' | '_parent' | '_top' | '_unfencedTop' = '_blank', features? : string ) => {
                window.open( url, target, features );
            },

            // frontend specific
            openPanel: ( panelConfig : PanelPropsWithPanelType ) => {
                void this.props?.openPanel( panelConfig );
            },

            // frontend specific
            openPanelForItemType: ( rootItemSchema : Schema, rootItemTypeName : string, rootItemIdKeyOrCreateNewItemConfig : string | number | Required<PanelPropsWithPanelType>[ 'createNewItem'] ) => {
                const panelProps : PanelProps = {
                    schema: rootItemSchema
                };

                if ( typeof rootItemIdKeyOrCreateNewItemConfig === 'number' ) {
                    panelProps.rootItemId = rootItemIdKeyOrCreateNewItemConfig;
                }
                else if ( typeof rootItemIdKeyOrCreateNewItemConfig === 'string' ) {
                    panelProps.rootItemType = rootItemTypeName;
                    panelProps.rootItemKey = rootItemIdKeyOrCreateNewItemConfig;
                }
                else {
                    panelProps.createNewItem = rootItemIdKeyOrCreateNewItemConfig;
                }

                void this.props?.openPanelForItemType(
                    rootItemSchema,
                    rootItemTypeName,
                    panelProps,
                    undefined,
                    undefined,
                    'nongeneric'
                );
            },

            // frontend and backend common
            propertyExists: ( object : Item | Reference, propertyTypeName : string ) : boolean => {
                const startTime   = this.debug ? performance.now() : 0,
                      errorPrefix = `${commonErrorPrefix}propertyExists: `;

                if ( this.debug ) {
                    console.groupCollapsed( `%c${'helperFunction - %cpropertyExists%c'.asLogLine( 94 )}`, 'font-weight: normal;', `font-weight: bold;`, 'font-weight: normal;' );
                    console.log( '    object', JSON.parse( JSON.stringify( object ) ) );
                    console.log( '    propertyTypeName', propertyTypeName );
                }

                // parameter validation
                if ( !object || !( object instanceof Item || object instanceof Reference ) ) {
                    console.groupEnd();
                    throw ( new Error( `${errorPrefix}first parameter (item|reference) must be an Item or a Reference` ) );
                }
                if ( typeof propertyTypeName !== 'string' ) {
                    console.groupEnd();
                    throw ( new Error( `${errorPrefix}second parameter (propertyTypeName) must be a string` ) );
                }

                let retVal : boolean = false,
                    objectPropertySet : PropertySet | ReferencePropertySet | undefined;

                if ( object instanceof Item ) {
                    objectPropertySet = object.itemType.propertySets.find( pS => pS.name === propertyTypeName );
                }
                else {
                    objectPropertySet = object.referenceSet.propertySets.find( pS => pS.name === propertyTypeName );
                }

                if ( objectPropertySet ) {
                    if ( objectPropertySet.isEnumerated ) {
                        retVal = object.enumProperties.has( objectPropertySet.propertyTypeId );
                    }
                    else {
                        retVal = object.properties.has( objectPropertySet.propertyTypeId ) && !object.properties.get( objectPropertySet.propertyTypeId )!.isDeleted();
                    }
                }

                if ( this.debug ) {
                    console.log( '    retVal', retVal );
                    console.log( `%c${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 93 ), `font-weight: bold;`, 'font-weight: unset;' );
                    console.groupEnd();
                }

                return retVal;
            },

            redirectToLogin: ( userToLogin? : string ) => {
                window.onbeforeunload = null;
                window.localStorage.clear();

                if ( userToLogin ) {
                    localStorage.setItem( 'lastUser', userToLogin );
                }

                window.location.href = '/';
            },

            showLoadingMask: () : void => {
                this.props.startRequest?.( () => {
                }, this.uid, 'processing' );
            },

            hideLoadingMask: () : void => {
                this.props.endRequest?.( () => {
                }, this.uid, 'processing' );
            },

            // frontend specific
            showToast: ( title : string, message : string, severity : 'success' | 'info' | 'warn' | 'error' = 'info', sticky : boolean = false ) : void => {
                const startTime   = this.debug ? performance.now() : 0,
                      errorPrefix = `${commonErrorPrefix}showToast: `,
                      toastParams = JSON.stringify( { title, message, severity, sticky } );

                if ( this.debug ) {
                    console.groupCollapsed( `%c${'helperFunction - %cshowToast%c'.asLogLine( 94 )}`, 'font-weight: normal;', `font-weight: bold;`, 'font-weight: normal;' );
                    console.log( '    title', title );
                    console.log( '    message', message );
                    console.log( '    severity', severity );
                    console.log( '    sticky', sticky );
                }

                // parameter validation
                if ( !title || typeof title !== 'string' ) {
                    console.groupEnd();
                    throw ( new Error( `${errorPrefix}first parameter (title) must contain a string` ) );
                }
                else if ( !message || typeof message !== 'string' ) {
                    console.groupEnd();
                    throw ( new Error( `${errorPrefix}second parameter (message) must contain a string` ) );
                }
                else if ( ![ 'success', 'info', 'warn', 'error' ].includes( severity ) ) {
                    console.groupEnd();
                    throw ( new Error( `${errorPrefix}third parameter (severity) must contain one of 'success', 'info', 'warn' or 'error'` ) );
                }
                else if ( typeof sticky !== 'boolean' ) {
                    console.groupEnd();
                    throw ( new Error( `${errorPrefix}4th parameter (sticky) must contain a boolean` ) );
                }

                if ( !this.shownToasts[ toastParams ] ) {
                    this.shownToasts[ toastParams ] = true;

                    this.props.setMessage(
                        mode === 'standalone' ? uid : dataContext,
                        'toast',
                        `showToast`,
                        {
                            severity,
                            sticky,
                            closable: sticky,
                            summary : parse( title ),
                            detail  : parse( message )
                        }
                    );

                    setTimeout(
                        () => {
                            delete this.shownToasts[ toastParams ];
                        },
                        1000
                    );
                }

                if ( this.debug ) {
                    console.log( `%c${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 93 ), `font-weight: bold;`, 'font-weight: unset;' );
                    console.groupEnd();
                }
            },

            // frontend specific
            showConfirm: async ( title : string, content : string, okCallback : () => Promise<void>, cancelCallback? : () => Promise<void>, icon : string = 'pi pi-question-circle yn-blue' ) : Promise<void> => {
                const startTime   = this.debug ? performance.now() : 0,
                      errorPrefix = `${commonErrorPrefix}showConfirm: `;

                if ( this.debug ) {
                    console.groupCollapsed( `%c${'helperFunction - %cshowConfirm%c'.asLogLine( 94 )}`, 'font-weight: normal;', `font-weight: bold;`, 'font-weight: normal;' );
                    console.log( '    title', title );
                    console.log( '    text', content );
                    console.log( '    okCallback', okCallback );
                    console.log( '    cancelCallback', cancelCallback );
                }

                // parameter validation
                if ( !title || typeof title !== 'string' ) {
                    console.groupEnd();
                    throw ( new Error( `${errorPrefix}first parameter (title) must contain a string` ) );
                }
                else if ( !content || typeof content !== 'string' ) {
                    console.groupEnd();
                    throw ( new Error( `${errorPrefix}second parameter (content) must contain a string` ) );
                }
                else if ( !okCallback || typeof okCallback !== 'function' ) {
                    console.groupEnd();
                    throw ( new Error( `${errorPrefix}third parameter (okCallback) must contain a function` ) );
                }
                else if ( cancelCallback && typeof cancelCallback !== 'function' ) {
                    console.groupEnd();
                    throw ( new Error( `${errorPrefix}fourth parameter (cancelCallback) must contain a function` ) );
                }

                await new Promise( ( resolve ) => {
                    const acceptFnc = async () => {
                              await okCallback();
                              resolve( true );
                          },
                          rejectFnc = async () => {
                              await cancelCallback?.();
                              resolve( true );
                          };

                    confirmPopup( {
                        target         : event?.currentTarget ?? event?.target ?? document.getElementById( `${uid}_form` ) ?? document.body,
                        message        : parse( `<span class="yn-confirmpopup-title yn-blue">${__( title )}</span><br /><br />${__( content )}` ),
                        icon,
                        style          : {
                            margin  : 0,
                            width   : 'auto',
                            minWidth: '25rem',
                            maxWidth: '50rem'
                        },
                        acceptLabel    : __( 'YN.base.common.ok' ),
                        acceptClassName: 'p-button p-button-outlined',
                        rejectLabel    : __( 'YN.base.common.cancel' ),
                        rejectClassName: 'p-button p-button-outlined',
                        accept         : () => {
                            void acceptFnc();
                        },
                        reject         : () => {
                            void rejectFnc();
                        }
                    } );
                } );

                if ( this.debug ) {
                    console.log( `%c${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 93 ), `font-weight: bold;`, 'font-weight: unset;' );
                    console.groupEnd();
                }
            },

            getHash: () : string => {
                return uuidV4().substring( 0, 8 );
            },

            // frontend specific
            getLocalData: () : { items : Item[], references : Reference[] } => {
                const localDataContext = `${dataContext}_localCopy`,
                      retVal           = {
                          items     : Object.values( global.data[ dataContext ]?.items ?? {} ).map( item => item.clone( undefined, undefined, localDataContext ) ),
                          references: Object.values( global.data[ dataContext ]?.references ?? {} ).map( reference => reference.clone( undefined, undefined, localDataContext ) )
                      };

                delete global.data[ localDataContext ];

                return retVal;
            },

            // frontend specific
            loadQuery: async ( queryIdOrKey : number | string, parameters : Parameters = {} ) : Promise<LoadQueryResultResult | undefined> => {
                const startTime   = this.debug ? performance.now() : 0,
                      errorPrefix = `${commonErrorPrefix}loadQuery: `;

                let retVal : LoadQueryResultResult | undefined = undefined;

                if ( this.debug ) {
                    console.groupCollapsed( `%c${'helperFunction - %cloadQuery%c'.asLogLine( 94 )}`, 'font-weight: normal;', `font-weight: bold;`, 'font-weight: normal;' );
                    console.log( '    queryIdOrKey', queryIdOrKey );
                }

                // parameter validation
                if ( !queryIdOrKey ) {
                    console.groupEnd();
                    throw ( new Error( `${errorPrefix}first parameter (id|key) must contain a number or string as the id or key of a CFG:Query item` ) );
                }

                let response : AxiosResponse<any, any> | null = null;

                try {
                    response = await xhr.post(
                        `${BASE_URL}/api/data/loadQueryResult`,
                        typeof queryIdOrKey === 'number'
                            ? {
                                id: queryIdOrKey,
                                parameters
                            } as LoadQueryResultParameters
                            : {
                                key: queryIdOrKey,
                                parameters
                            } as LoadQueryResultParameters,
                        undefined,
                        uid + '_action',
                        'loadQueryResult',
                        undefined,
                        'throw'
                    );
                }
                catch ( e ) {
                    // do nothing
                }

                if ( response?.data ) {
                    retVal = response.data as LoadQueryResultResult;
                }

                if ( this.debug ) {
                    console.log( `%c${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 93 ), `font-weight: bold;`, 'font-weight: unset;' );
                    console.groupEnd();
                }

                return retVal;
            }
        }
    },

    /**
     * Gets the details out of an error thrown while executing a dynamic function.
     *
     * @param e
     * @param functionBody
     *
     * @private
     */
    getDynamicFunctionErrorDetail( e : any, functionBody : string ) : string {
        let retVal = String( e );

        if ( e instanceof Error ) {
            const errorStack    = e?.stack?.split( '\n' ) ?? [],
                  errorPosition = errorStack[ 1 ] && errorStack[ 1 ].indexOf( '<anonymous>' ) !== -1
                      ? ( errorStack[ 1 ] ? errorStack[ 1 ].replace( /.*<anonymous>:(.*)\)/, '$1' ) : '' ).split( ':' ).map( v => Number( v ) )
                      : ( errorStack[ 2 ] ? errorStack[ 2 ].replace( /.*<anonymous>:(.*)\)/, '$1' ) : '' ).split( ':' ).map( v => Number( v ) );

            retVal = e.toHTML();

            if ( errorPosition.length === 2 ) {
                errorPosition[ 0 ] = Math.max( errorPosition[ 0 ] - 7, 0 );
                errorPosition[ 1 ] = Math.max( errorPosition[ 1 ] - 22, 0 );

                const functionBodyLines = functionBody.split( '\n' ),
                      errorLine         = functionBodyLines?.[ errorPosition[ 0 ] ];

                if ( errorLine ) {
                    let contextOffset = 0,
                        contextLength : number | undefined

                    if ( errorLine.length > this.errorContextMaxLength ) {
                        if ( errorPosition[ 1 ] - this.errorContextMaxLength / 2 < 0 ) {
                            contextLength = this.errorContextMaxLength - 3;
                        }
                        else if ( errorPosition[ 1 ] + this.errorContextMaxLength / 2 > errorLine.length ) {
                            contextOffset = errorLine.length - ( this.errorContextMaxLength - 3 );
                        }
                        else {
                            contextOffset = errorPosition[ 1 ] - ( this.errorContextMaxLength / 2 - 3 );
                            contextLength = this.errorContextMaxLength - 6;
                        }
                    }

                    retVal += '<hr class="yn-error-ruler" /><div class="yn-error-message-monospace">';

                    if ( errorPosition[ 0 ] > 0 ) {
                        retVal += '...<br />';
                    }
                    if ( contextOffset ) {
                        retVal += '...';
                    }

                    retVal += errorLine.substring( contextOffset, contextLength ).replaceAll( ' ', '&nbsp;' );

                    if ( contextLength ) {
                        retVal += '...';
                    }

                    retVal += `<br/>${'&nbsp;'.repeat( errorPosition[ 1 ] - ( contextOffset ? contextOffset - 3 : 0 ) )}<div class="yn-error-position-marker fas fa-arrow-up"></div>`;

                    if ( errorPosition[ 0 ] < functionBodyLines.length - 1 ) {
                        retVal += '<br />...';
                    }

                    retVal += '</div>';
                }
            }
        }

        return retVal;
    },

    /**
     * Applies the changes on the resolvedDataObjects to global data.
     *
     * @param functionDataContext
     * @param resolvedDataObjects
     * @param clonedObjectsIdMappings
     * @param props
     *
     * @private
     */
    applyDynamicFunctionDataChanges( functionDataContext : string, resolvedDataObjects : ResolvedDataObjects, clonedObjectsIdMappings : ClonedObjectsIdMappings, props : ComponentProps = this.props ) {
        const startTime                          = this.debug ? performance.now() : 0,
              {
                  uid,
                  schema,
                  dataContext
              }                                  = props,
              additionalElusiveDataContexts      = ( props.additionalElusiveDataContexts ?? [] ).concat( [ uid ] ),
              resolvedDataObjectsWithoutInfoKeys = ( Object.keys( resolvedDataObjects ) as string[] ).reduce(
                  ( innerRetVal, dataObjectLabel ) => {
                      if ( !dataObjectLabel.match( /Info$/gm ) ) {
                          innerRetVal.push( dataObjectLabel );
                      }
                      return innerRetVal;
                  },
                  [] as string[]
              );

        let items : Item[]           = [],
            references : Reference[] = [];

        if ( this.debug ) {
            console.groupCollapsed( `%c${`%cActionMixin - %capplyDynamicFunctionDataChanges%c`.asLogLine( 97 )}`, `font-weight: normal;`, `font-weight: bold;`, `color: #${debugColorScheme.applyDynamicFunctionDataChanges}; font-weight: bold;`, 'color: unset; font-weight: normal;' );
            console.log( '    functionDataContext', functionDataContext );
            console.log( '    resolvedDataObjects', JSON.parse( JSON.stringify( resolvedDataObjects ) ) );
            console.log( '    clonedObjectsIdMappings', JSON.parse( JSON.stringify( clonedObjectsIdMappings ) ) );
        }

        if ( global.data[ functionDataContext ] && global.data[ functionDataContext ].schema === schema ) {
            items = Object.values( global.data[ functionDataContext ].items ) as Item[];
            references = Object.values( global.data[ functionDataContext ].references ) as Reference[];
        }

        for ( const item of items ) {
            const itemUpdateJson = item.toUpdateJSON( 'modified' );

            let originalItemState : string = '';

            for ( const dataObjectLabel of resolvedDataObjectsWithoutInfoKeys ) {
                const resolvedDataObject = resolvedDataObjects[ dataObjectLabel ];

                if ( Array.isArray( resolvedDataObject ) ) {
                    let index = ( resolvedDataObjects[ `${dataObjectLabel}Info` ].state as ResolvedDataObjectState[] ).findIndex( rDO1 => rDO1.itemId === item.id );

                    // try the old way (resolvedDataObjects order is the same as resolvedDataObjects[ `${dataObjectLabel}Info` ].state)
                    if ( index === -1 ) {
                        index = resolvedDataObject.findIndex( rDO1 => rDO1.item.id === item.id );
                    }

                    if ( index !== -1 ) {
                        originalItemState = ( resolvedDataObjects[ `${dataObjectLabel}Info` ].state as ResolvedDataObjectState[] )[ index ].item;
                        break;
                    }
                }
                else if ( ( resolvedDataObjects[ `${dataObjectLabel}Info` ].state as ResolvedDataObjectState ).itemId === item.id || resolvedDataObject.item?.id === item.id ) {
                    originalItemState = ( resolvedDataObjects[ `${dataObjectLabel}Info` ].state as ResolvedDataObjectState ).item;
                    break;
                }
            }

            // has changed?
            if ( originalItemState !== JSON.stringify( itemUpdateJson ) ) {
                const mappedItemId = clonedObjectsIdMappings.items[ item.id ] ?? item.id;

                // update item
                if ( global.data[ dataContext ].items[ mappedItemId ] ) {
                    if ( item.isDeleted() ) {
                        if ( this.debug ) {
                            console.log( '    item' );
                            console.log( '        itemId', mappedItemId );
                            console.log( '        -> delete' );
                        }

                        props.deleteItem(
                            dataContext,
                            mappedItemId,
                            true,
                            additionalElusiveDataContexts,
                            undefined,
                            true,
                            false
                        );
                    }
                    else {
                        if ( this.debug ) {
                            console.log( '    item' );
                            console.log( '        itemId', mappedItemId );
                            console.log( '        -> update' );
                        }

                        // INFO: changed to fullData
                        //   see also Form 8352
                        //const itemData = this.getUpdateDataWithRemovedNewProperties( global.data[ dataContext ].items[ mappedItemId ], item, false );
                        const itemData = this.getUpdateDataWithRemovedNewProperties( global.data[ dataContext ].items[ mappedItemId ], item );

                        if ( this.debug ) {
                            console.log( '                data', itemData );
                        }

                        props.updateItem(
                            dataContext,
                            mappedItemId,
                            itemData,
                            additionalElusiveDataContexts
                        );
                    }
                }

                // add existing item unmodified and apply modifications if
                else if ( item.id > 0 ) {
                    if ( this.debug ) {
                        console.log( '    item' );
                        console.log( '        itemId', item.id );
                        console.log( '        -> add' );
                    }

                    let itemData : ItemAttributesSettable | undefined;

                    if ( item.isModified() ) {
                        itemData = item.toUpdateJSON( 'modified' );

                        item.reset();
                    }

                    if ( this.debug ) {
                        console.log( '                data', { ...item.toJSON( 'all' ), ...itemData } );
                    }

                    props.addItem(
                        dataContext,
                        { ...item.toJSON( 'all' ) } as ItemAttributesForCreation,
                        schema,
                        additionalElusiveDataContexts
                    );

                    if ( itemData ) {
                        props.updateItem(
                            dataContext,
                            item.id,
                            itemData,
                            additionalElusiveDataContexts
                        );

                        item.update( itemData );
                    }
                }

                // add new item
                else if ( !item.isDeleted() ) {
                    if ( this.debug ) {
                        console.log( '    item' );
                        console.log( '        itemId', item.id );
                        console.log( '        -> create' );
                    }

                    const newItem  = this.props.addItem(
                              dataContext,
                              { itemTypeId: item.itemTypeId } as ItemAttributesForCreation,
                              schema,
                              additionalElusiveDataContexts
                          ),
                          itemData = item.toUpdateJSON( 'all' );

                    if ( this.debug ) {
                        console.log( '            data', {
                            ...itemData,
                            itemTypeId: item.itemTypeId
                        } );
                        console.log( '            new itemId', newItem.id );
                    }

                    props.updateItem(
                        dataContext,
                        newItem.id,
                        itemData,
                        additionalElusiveDataContexts
                    );

                    // add id mapping
                    clonedObjectsIdMappings.items[ item.id ] = newItem.id;
                }
            }
        }

        for ( const reference of references ) {
            const referenceUpdateJson = reference.toUpdateJSON( 'modified' );

            let originalReferenceState : string = '';

            for ( const dataObjectLabel of resolvedDataObjectsWithoutInfoKeys ) {
                const resolvedDataObject = resolvedDataObjects[ dataObjectLabel ];

                if ( Array.isArray( resolvedDataObject ) ) {
                    let index = ( resolvedDataObjects[ `${dataObjectLabel}Info` ].state as ResolvedDataObjectState[] ).findIndex( rDO1 => rDO1.referenceId === reference.id );

                    // try the old way (resolvedDataObjects order is the same as resolvedDataObjects[ `${dataObjectLabel}Info` ].state)
                    if ( index === -1 ) {
                        index = resolvedDataObject.findIndex( rDO1 => rDO1.reference?.id === reference.id );
                    }

                    if ( index !== -1 ) {
                        originalReferenceState = ( resolvedDataObjects[ `${dataObjectLabel}Info` ].state as ResolvedDataObjectState[] )[ index ].reference;
                        break;
                    }
                }
                else if ( ( resolvedDataObjects[ `${dataObjectLabel}Info` ].state as ResolvedDataObjectState ).referenceId === reference.id || resolvedDataObject.reference?.id === reference.id ) {
                    originalReferenceState = ( resolvedDataObjects[ `${dataObjectLabel}Info` ].state as ResolvedDataObjectState ).reference;
                    break;
                }
            }

            // has changed?
            if ( originalReferenceState !== JSON.stringify( referenceUpdateJson ) ) {
                const mappedReferenceId = clonedObjectsIdMappings.references[ reference.id ] ?? reference.id;

                // update reference
                if ( global.data[ dataContext ].references[ mappedReferenceId ] ) {
                    if ( reference.isDeleted() ) {
                        if ( this.debug ) {
                            console.log( '    reference' );
                            console.log( '        referenceId', mappedReferenceId );
                            console.log( '        referenceSetId', reference.referenceSetId );
                            console.log( '        -> delete' );
                        }

                        props.deleteReference(
                            dataContext,
                            mappedReferenceId,
                            true,
                            additionalElusiveDataContexts
                        );
                    }
                    else {
                        if ( this.debug ) {
                            console.log( '    reference' );
                            console.log( '        referenceId', mappedReferenceId );
                            console.log( '        referenceSetId', reference.referenceSetId );
                            console.log( '        -> update' );
                        }

                        // INFO: changed to fullData
                        //   see also Form 8397
                        //const referenceData = this.getUpdateDataWithRemovedNewProperties( global.data[ dataContext ].references[ mappedReferenceId ], reference, false );
                        const referenceData = this.getUpdateDataWithRemovedNewProperties( global.data[ dataContext ].references[ mappedReferenceId ], reference );

                        if ( this.debug ) {
                            console.log( '                data', referenceData );
                        }

                        props.updateReference(
                            dataContext,
                            mappedReferenceId,
                            referenceData,
                            additionalElusiveDataContexts
                        );
                    }
                }

                // add existing reference unmodified and apply modifications if
                else if ( reference.id > 0 ) {
                    if ( this.debug ) {
                        console.log( '    reference' );
                        console.log( '        referenceId', reference.id );
                        console.log( '        referenceSetId', reference.referenceSetId );
                        console.log( '        -> add' );
                    }

                    let referenceData : ReferenceAttributesSettable | undefined;

                    if ( reference.isModified() ) {
                        referenceData = reference.toUpdateJSON( 'modified' );

                        reference.reset();
                    }

                    if ( this.debug ) {
                        console.log( '                data', { ...reference.toJSON( 'all' ), ...referenceData } );
                    }

                    props.addReference(
                        dataContext,
                        { ...reference.toJSON( 'all' ) } as ReferenceAttributesForCreation,
                        schema,
                        additionalElusiveDataContexts
                    );

                    if ( referenceData ) {
                        props.updateReference(
                            dataContext,
                            reference.id,
                            referenceData,
                            additionalElusiveDataContexts
                        );

                        reference.update( referenceData );
                    }
                }

                // add new reference
                else if ( !reference.isDeleted() ) {
                    if ( this.debug ) {
                        console.log( '    reference' );
                        console.log( '        referenceId', reference.id );
                        console.log( '        referenceSetId', reference.referenceSetId );
                        console.log( '        -> create' );
                    }

                    const newReference  = props.addReference(
                              dataContext,
                              {
                                  referenceSetId: reference.referenceSetId,
                                  srcItemId     : clonedObjectsIdMappings.items[ reference.srcItemId ] ?? reference.srcItemId,
                                  destItemId    : clonedObjectsIdMappings.items[ reference.destItemId ] ?? reference.destItemId
                              } as ReferenceAttributesForCreation,
                              schema,
                              additionalElusiveDataContexts
                          ),
                          referenceData = reference.toUpdateJSON( 'all' );

                    if ( this.debug ) {
                        console.log( '            data', {
                            ...referenceData,
                            referenceSetId: reference.referenceSetId,
                            srcItemId     : clonedObjectsIdMappings.items[ reference.srcItemId ] ?? reference.srcItemId,
                            destItemId    : clonedObjectsIdMappings.items[ reference.destItemId ] ?? reference.destItemId
                        } );
                        console.log( '            new referenceId', newReference.id );
                    }

                    props.updateReference(
                        dataContext,
                        newReference.id,
                        referenceData,
                        additionalElusiveDataContexts
                    );
                }
            }
        }

        if ( this.debug ) {
            console.log( `%c${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 96 ), `color: #${debugColorScheme.applyDynamicFunctionDataChanges}; font-weight: bold;`, 'color: unset; font-weight: unset;' );
            console.groupEnd();
        }
    },

    /**
     * Applies the changes on the additional reference data to global data.
     *
     * @param value
     * @param props
     * @param itemIdMapping
     *
     * @private
     */
    applyAdditionalReferenceDataChanges( value : ReferenceValue | ReferenceValue[], props : AllProps = this.props, itemIdMapping : {
        [ id : number ] : number
    }                                                                                                = {} ) {
        const startTime                     = this.debug ? performance.now() : 0,
              {
                  uid,
                  schema,
                  dataContext
              }                             = props,
              additionalElusiveDataContexts = props.additionalElusiveDataContexts.concat( [ uid ] ),
              referenceValues               = !Array.isArray( value ) ? [ value ] : value,
              items : Item[]                = [],
              references : Reference[]      = [];

        if ( this.debug ) {
            console.groupCollapsed( `%c${`%cActionMixin - %capplyAdditionalReferenceDataChanges%c`.asLogLine( 97 )}`, `font-weight: normal;`, `font-weight: bold;`, `color: #${debugColorScheme.applyAdditionalReferenceDataChanges}; font-weight: bold;`, 'color: unset; font-weight: normal;' );
            console.log( '    value', JSON.parse( JSON.stringify( value ) ) );
        }

        for ( const referenceValue of referenceValues ) {
            if ( referenceValue.additionalData ) {
                for ( const item of referenceValue.additionalData.items ) {
                    if ( !items.includes( item ) ) {
                        items.push( item );
                    }
                }
                for ( const reference of referenceValue.additionalData.references ) {
                    if ( !references.includes( reference ) ) {
                        references.push( reference );
                    }
                }
            }
        }

        for ( const item of items ) {
            // update existing/new item
            if ( global.data[ dataContext ].items[ item.id ] ) {
                const itemToUpdate = global.data[ dataContext ].items[ item.id ];

                if ( item.isDeleted() ) {
                    if ( item.statusId !== itemToUpdate.statusId ) {
                        if ( this.debug ) {
                            console.log( '    item' );
                            console.log( '        itemId', item.id );
                            console.log( '        -> delete existing' );
                        }

                        props.deleteItem(
                            dataContext,
                            item.id,
                            true,
                            additionalElusiveDataContexts,
                            undefined,
                            true,
                            false
                        );
                    }
                }
                else {
                    if ( JSON.stringify( item.toUpdateJSON( 'modified' ) ) !== JSON.stringify( itemToUpdate.toUpdateJSON( 'modified' ) ) ) {
                        if ( this.debug ) {
                            console.log( '    item' );
                            console.log( '        itemId', item.id );
                            console.log( '        -> update' );
                        }

                        const itemData = this.getUpdateDataWithRemovedNewProperties( itemToUpdate, item, false );

                        if ( this.debug ) {
                            console.log( '                data', itemData );
                        }

                        this.props.updateItem(
                            dataContext,
                            item.id,
                            itemData,
                            additionalElusiveDataContexts
                        );
                    }
                }
            }
            // add existing item
            else if ( item.id > 0 ) {
                if ( this.debug ) {
                    console.log( '    item' );
                    console.log( '        itemId', item.id );
                    console.log( '        -> add' );
                }

                let itemData : ItemAttributesSettable | undefined;

                if ( item.isModified() ) {
                    itemData = item.toUpdateJSON( 'modified' );

                    item.reset();
                }

                if ( this.debug ) {
                    console.log( '                data', itemData );
                }

                this.props.addItem(
                    dataContext,
                    { ...item.toJSON( 'all' ) },
                    schema,
                    additionalElusiveDataContexts
                );

                if ( itemData ) {
                    this.props.updateItem(
                        dataContext,
                        item.id,
                        itemData,
                        additionalElusiveDataContexts
                    );

                    item.update( itemData );
                }
            }
            // add new item
            else {
                if ( this.debug ) {
                    console.log( '    item' );
                    console.log( '        itemId', item.id );
                    console.log( '        -> create' );
                }

                const newItem  = this.props.addItem(
                          dataContext,
                          { itemTypeId: item.itemTypeId },
                          schema,
                          additionalElusiveDataContexts
                      ),
                      itemData = item.toUpdateJSON( 'all' );

                if ( this.debug ) {
                    console.log( '            data', itemData );
                }

                this.props.updateItem(
                    dataContext,
                    newItem.id,
                    itemData,
                    additionalElusiveDataContexts
                );

                // add id mapping
                itemIdMapping[ item.id ] = newItem.id;
            }
        }

        // handle deleted new references
        for ( const reference of Object.values( global.data[ dataContext ].references ) ) {
            if ( reference.isNew() && !references.find( r => r.id === reference.id ) ) {
                if ( this.debug ) {
                    console.log( '    reference' );
                    console.log( '        referenceId', reference.id );
                    console.log( '        referenceSetId', reference.referenceSetId );
                    console.log( '        -> delete new' );
                }

                this.props.deleteReference(
                    dataContext,
                    reference.id,
                    true,
                    additionalElusiveDataContexts
                );
            }
        }

        for ( const reference of references ) {
            // update existing/new reference
            if ( global.data[ dataContext ].references[ reference.id ] ) {
                const referenceToUpdate = global.data[ dataContext ].references[ reference.id ];

                if ( reference.isDeleted() ) {
                    if ( reference.statusId !== referenceToUpdate.statusId ) {
                        if ( this.debug ) {
                            console.log( '    reference' );
                            console.log( '        referenceId', reference.id );
                            console.log( '        referenceSetId', reference.referenceSetId );
                            console.log( '        -> delete existing' );
                        }

                        this.props.deleteReference(
                            dataContext,
                            reference.id,
                            true,
                            additionalElusiveDataContexts
                        );
                    }
                }
                else {
                    if ( JSON.stringify( reference.toUpdateJSON( 'modified' ) ) !== JSON.stringify( referenceToUpdate.toUpdateJSON( 'modified' ) ) ) {
                        if ( this.debug ) {
                            console.log( '    reference' );
                            console.log( '        referenceId', reference.id );
                            console.log( '        referenceSetId', reference.referenceSetId );
                            console.log( '        -> update' );
                        }

                        const referenceData = this.getUpdateDataWithRemovedNewProperties( referenceToUpdate, reference, false );

                        if ( this.debug ) {
                            console.log( '                data', referenceData );
                        }

                        this.props.updateReference(
                            dataContext,
                            reference.id,
                            referenceData,
                            additionalElusiveDataContexts
                        );
                    }
                }
            }
            // add existing reference
            else if ( reference.id > 0 ) {
                if ( this.debug ) {
                    console.log( '    reference' );
                    console.log( '        referenceId', reference.id );
                    console.log( '        referenceSetId', reference.referenceSetId );
                    console.log( '        -> add' );
                }

                let referenceData : ReferenceAttributesSettable | undefined;

                if ( reference.isModified() ) {
                    referenceData = reference.toUpdateJSON( 'modified' );

                    reference.reset();
                }

                if ( this.debug ) {
                    console.log( '                data', referenceData );
                }

                this.props.addReference(
                    dataContext,
                    { ...reference.toJSON( 'all' ) },
                    schema,
                    additionalElusiveDataContexts
                );

                if ( referenceData ) {
                    this.props.updateReference(
                        dataContext,
                        reference.id,
                        referenceData,
                        additionalElusiveDataContexts
                    );

                    reference.update( referenceData );
                }
            }
            // add new reference
            else {
                if ( this.debug ) {
                    console.log( '    reference' );
                    console.log( '        referenceId', reference.id );
                    console.log( '        referenceSetId', reference.referenceSetId );
                    console.log( '        -> create' );
                }

                const newReference  = this.props.addReference(
                          dataContext,
                          {
                              referenceSetId: reference.referenceSetId,
                              srcItemId     : itemIdMapping[ reference.srcItemId ] ?? reference.srcItemId,
                              destItemId    : itemIdMapping[ reference.destItemId ] ?? reference.destItemId
                          },
                          schema,
                          additionalElusiveDataContexts
                      ),
                      referenceData = reference.toUpdateJSON( 'all' );

                if ( this.debug ) {
                    console.log( '            data', referenceData );
                }

                this.props.updateReference(
                    dataContext,
                    newReference.id,
                    referenceData,
                    additionalElusiveDataContexts
                );
            }
        }

        if ( this.debug ) {
            console.log( `%c${Math.round( 1000 * ( performance.now() - startTime ) ) / 1000} ms%c`.asLogLine( 96 ), `color: #${debugColorScheme.applyAdditionalReferenceDataChanges}; font-weight: bold;`, 'color: unset; font-weight: unset;' );
            console.groupEnd();
        }
    },

    getUpdateDataWithRemovedNewProperties<K extends Item | Reference>( originalObject : K, changedObject : K, fullData : boolean = true ) : K extends Item ? ItemAttributesSettable : ReferenceAttributesSettable {
        const originalEnumProperties = originalObject.enumProperties[ Symbol.iterator ](),
              originalProperties     = originalObject.properties[ Symbol.iterator ](),
              deletedStatusId        = metaModel[ schema ].getStatus( 'deleted' )!.id;

        let retVal : ItemAttributesSettable | ReferenceAttributesSettable;

        if ( changedObject instanceof Item ) {
            retVal = ( fullData ? changedObject.toUpdateJSON( 'all' ) : changedObject.toUpdateJSON( 'modified' ) ) as ItemAttributesSettable;
        }
        else {
            retVal = ( fullData ? changedObject.toUpdateJSON( 'all' ) : changedObject.toUpdateJSON( 'modified' ) ) as ReferenceAttributesSettable;
        }

        // handle deleted new enumProperty properties
        for ( const [ propertyTypeId, enumProperty ] of originalEnumProperties ) {
            const originalEnumPropertyProperties = enumProperty.properties[ Symbol.iterator ](),
                  changedEnumProperty            = changedObject.enumProperties.get( propertyTypeId );

            for ( const [ key, property ] of originalEnumPropertyProperties ) {
                if ( property.isNew() && !changedEnumProperty?.properties.has( key ) ) {
                    if ( !retVal.enumProperties ) {
                        retVal.enumProperties = {};
                    }
                    if ( !retVal.enumProperties[ propertyTypeId ] ) {
                        retVal.enumProperties[ propertyTypeId ] = {
                            properties: {}
                        };
                    }
                    retVal.enumProperties[ propertyTypeId ].properties![ key ] = {
                        statusId: deletedStatusId
                    };
                }
            }
        }

        // handle deleted new properties
        for ( const [ propertyTypeId, property ] of originalProperties ) {
            if ( property.isNew() && !changedObject.properties.has( propertyTypeId ) ) {
                if ( !retVal.properties ) {
                    retVal.properties = {};
                }
                retVal.properties[ propertyTypeId ] = {
                    statusId: deletedStatusId
                };
            }
        }

        return retVal;
    },

    getResolvedDataObjectsWithoutInfoKeys( resolvedDataObjects : ResolvedDataObjects ) : MappedResolvedDataObjects {
        return ( Object.keys( resolvedDataObjects ) as string[] ).reduce(
            ( innerRetVal, dataObjectLabel ) => {
                if ( !dataObjectLabel.match( /Info$/gm ) ) {
                    innerRetVal[ dataObjectLabel ] = resolvedDataObjects[ dataObjectLabel ];
                }
                return innerRetVal;
            },
            {} as MappedResolvedDataObjects
        );
    },

    applyDynamicFunctionVariablesChanges( props : ComponentProps = this.props ) {
        const changedVariables = objectUtils.getDiffKeys(
            props.elusiveData?.variables ?? {},
            this.variables ?? {}
        );

        if ( changedVariables.length ) {
            props.setElusiveData( props.uid, { variables: this.variables } );
        }
    },

    applyDynamicFunctionTabIndexChanges( props : FormProps = this.props, resolvedLayoutElements : ResolvedLayoutElements ) {
        const changedActiveTabContainerIndexes = { ...props.activeTabContainerIndexes };

        let changed : boolean = false;

        for ( const formElement of ( Object.values( resolvedLayoutElements ) as TabContainerProperties[] ) ) {
            if (
                props.layoutElements[ formElement.id ].type === 'TABCONTAINER'
                && formElement.activeTabIndex !== undefined
                && ( changedActiveTabContainerIndexes[ formElement.id ] ?? 0 ) !== formElement.activeTabIndex
            ) {
                changed = true;
                changedActiveTabContainerIndexes[ formElement.id ] = formElement.activeTabIndex;
            }
        }

        if ( changed ) {
            props.setElusiveData( props.uid, { activeTabContainerIndexes: changedActiveTabContainerIndexes } );
        }
    }

}

// export the mixin object
export default actionMixin;

// and the interface for proper TypeScript usage of the mixin
type ActionMixin = typeof actionMixin;

export interface ActionMixinInterface extends ActionMixin {
}
